import type { Vector2d } from '@/lib/CanvasEngine/types'
import type { IGroup, IRenderer, PointerEventHandler } from '@/lib/CanvasEngine/backend/IRenderer'
import { BalloonHead, type BalloonHeadConfig } from './balloonHead'
import { BalloonConnectedLine, type BalloonConnectedLineConfig } from './balloonConnectedLine'
import { BalloonTag, type BalloonTagData } from './balloonTag'

/**
 * Balloon 전체 설정 인터페이스
 */
export interface BalloonConfig {
  color: string
  fillColor: string
  fontColor: string
  text: string
  size: number
  strokeWidth: number
  fontSize: number
  interactionMode: boolean
  isSelected: boolean
  isTransparentLine?: boolean
}

/**
 * Balloon 위치 정보 인터페이스
 */
export interface BalloonPositionInfo {
  balloonPos: Vector2d
  previousPosition: Vector2d
}

/**
 * Balloon 클래스
 * Head + ConnectedLine + Tag로 구성된 전체 Balloon 객체를 관리
 */
export class Balloon {
  public group: IGroup
  public head: BalloonHead
  public connectedLine: BalloonConnectedLine
  public tag: BalloonTag
  public previousPosition: Vector2d

  constructor(renderer: IRenderer, config: BalloonConfig, initialPosition: Vector2d) {
    this.group = renderer.createGroup()
    this.previousPosition = initialPosition

    // Head 생성
    const headConfig: BalloonHeadConfig = {
      color: config.color,
      fillColor: config.fillColor,
      fontColor: config.fontColor,
      text: config.text,
      size: config.size,
      strokeWidth: config.strokeWidth,
      fontSize: config.fontSize,
      interactionMode: config.interactionMode,
      isSelected: config.isSelected,
    }
    this.head = new BalloonHead(renderer, headConfig)

    // Connected Line 생성
    const lineConfig: BalloonConnectedLineConfig = {
      color: config.fontColor,
      strokeWidth: config.strokeWidth,
      isTransparent: config.isTransparentLine,
    }
    this.connectedLine = new BalloonConnectedLine(renderer, lineConfig)

    // Tag 생성
    this.tag = new BalloonTag(renderer)

    // Group에 요소들 추가
    this.group.add(this.connectedLine.line)
    this.group.add(this.head.rect)
    this.group.add(this.head.text)
    this.group.add(this.tag.group)

    // 초기 위치 설정
    this.head.setPosition(initialPosition)
  }

  /**
   * Balloon 위치 설정
   */
  public setPosition(pos: Vector2d): void {
    this.head.setPosition(pos)
    this.head.updateTextPosition()
  }

  /**
   * Balloon 위치 가져오기
   */
  public getPosition(): Vector2d {
    return this.head.getPosition()
  }

  /**
   * Balloon 색상 변경 (Head + Line)
   */
  public changeColor(color: string): void {
    this.head.changeColor(color)
    this.connectedLine.changeColor(color)
  }

  /**
   * Balloon Fill 색상 변경
   */
  public changeFillColor(color: string, opacity?: number): void {
    this.head.changeFillColor(color, opacity)
  }

  /**
   * Balloon Font 색상 변경
   */
  public changeFontColor(color: string): void {
    this.head.changeFontColor(color)
  }

  /**
   * Balloon Text 변경
   */
  public setText(text: string): void {
    this.head.setText(text)
    this.head.updateTextPosition()
  }

  /**
   * Balloon 원형/사각형 모양 변경
   */
  public setCircle(isCircle: boolean): void {
    this.head.setCircle(isCircle)
  }

  /**
   * Tag 설정
   */
  public setTag(tag: BalloonTagData): void {
    this.tag.setData(tag)
    this.updateTagPosition()
  }

  /**
   * Tag 숨김
   */
  public hideTag(): void {
    this.tag.hide()
  }

  /**
   * Connected Line Points 업데이트
   */
  public updateConnectedLinePoints(points: number[]): void {
    this.connectedLine.updatePoints(points)
    this.connectedLine.setVisible(true)
    this.connectedLine.moveToBottom()
  }

  /**
   * Tag 위치 업데이트
   */
  public updateTagPosition(): void {
    const balloonPos = this.head.getPosition()
    const balloonSize = this.head.getSize()
    this.tag.syncPosition(balloonPos, balloonSize)
  }

  /**
   * Balloon 전체 업데이트 (위치 + Text + Tag)
   */
  public updateAll(connectorPoints: number[]): void {
    this.head.updateTextPosition()
    this.updateConnectedLinePoints(connectorPoints)
    this.updateTagPosition()
  }

  /**
   * Balloon Draggable 설정
   */
  public setDraggable(draggable: boolean): void {
    this.head.setDraggable(draggable)
  }

  /**
   * Balloon이 드래그 중인지 확인
   */
  public isDragging(): boolean {
    return this.head.isDragging()
  }

  /**
   * 레이어 순서 변경 (맨 위로)
   */
  public moveToTop(): void {
    this.head.moveToTop()
    this.connectedLine.moveToBottom()
  }

  /**
   * Head 이벤트 리스너 등록
   */
  public onHead(eventName: string, handler: PointerEventHandler): void {
    this.head.on(eventName, handler)
  }

  /**
   * Connected Line 이벤트 리스너 등록
   */
  public onLine(eventName: string, handler: PointerEventHandler): void {
    this.connectedLine.on(eventName, handler)
  }

  /**
   * Balloon 크기 가져오기
   */
  public getSize(): number {
    return this.head.getSize()
  }

  /**
   * Balloon 절대 좌표 가져오기
   */
  public getAbsolutePosition(entityPos: Vector2d): Vector2d {
    return this.head.toAbsolutePosition(entityPos)
  }

  /**
   * Connected Line 표시/숨김
   */
  public setLineVisible(visible: boolean): void {
    this.connectedLine.setVisible(visible)
  }

  /**
   * Balloon 객체 정리
   */
  public destroy(): void {
    this.group.destroy()
  }
}

// 하위 모듈 export
export { BalloonHead, BalloonConnectedLine, BalloonTag }
export type { BalloonHeadConfig, BalloonConnectedLineConfig, BalloonTagData }
